import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import type { Lang } from "@/i18n";
import { hapticTap, openLink } from "@/tg/init";
import { getUser } from "@/tg/auth";
import {
  getMe,
  getSection,
  peekMe,
  peekSection,
  type Membership,
  type SectionItem,
} from "@/api";
import { LEVELS, REWARDS, pickLoc } from "@/data/membership";
import { SUBSCRIBE_URL, DONATE_URL } from "@/lib/links";
import Logo from "@/components/Logo";
import MyDonationsBlock from "@/components/MyDonationsBlock";
import FundraiserCarousel from "@/components/FundraiserCarousel";

export default function Home() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();

  const [me, setMe] = useState<Membership | null>(() => peekMe(lang) ?? null);
  const [fundraisings, setFundraisings] = useState<SectionItem[]>(
    () => peekSection("fundraisings", lang) ?? [],
  );

  const user = getUser();

  useEffect(() => {
    getMe(lang)
      .then(setMe)
      .catch(() => setMe(null));
    getSection("fundraisings", lang)
      .then(setFundraisings)
      .catch(() => setFundraisings([]));
  }, [lang]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col"
      style={{ background: "var(--tg-bg)" }}
    >
      <h1
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        Благодійний фонд Сергія Притули
      </h1>

      {/* Sticky header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          height: 56,
          background: "#ffffff",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <Logo size={34} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => {
              hapticTap();
              setLang(lang === "uk" ? "en" : ("uk" as Lang));
            }}
            aria-label="Switch language"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--tint)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              fontSize: 14,
              fontFamily: '"e-Ukraine", system-ui, sans-serif',
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {lang === "uk" ? "UA" : "EN"}
          </button>
          <button
            onClick={() => {
              hapticTap();
              openLink(DONATE_URL);
            }}
            aria-label="Donate"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--brand-blue)",
              color: "#ffffff",
              border: "none",
              fontSize: 14,
              fontFamily: '"e-Ukraine", system-ui, sans-serif',
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {t("donate")}
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ padding: "16px 16px 24px" }}>
        {/* 1. My Donations block */}
        <MyDonationsBlock user={user} />

        {/* 2. Membership card */}
        {me && (
          <div
            style={{
              background: "var(--tint)",
              borderRadius: "var(--r-card)",
              padding: 16,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontFamily: '"e-UkraineHead", system-ui, sans-serif',
                  fontSize: 18,
                  fontWeight: 600,
                  color: "var(--ink)",
                }}
              >
                {LEVELS.find((l) => l.level === me.level)?.name ??
                  (lang === "uk" ? `Рівень ${me.level}` : `Level ${me.level}`)}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "var(--muted)",
                  background: "#ffffff",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-pill)",
                  padding: "3px 9px",
                }}
              >
                {me.points} {t("points")} · {t("coming_soon")}
              </span>
            </div>
            <p
              style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0" }}
            >
              {me.monthly} ₴/{lang === "uk" ? "міс" : "mo"} ·{" "}
              {lang === "uk" ? `Рівень ${me.level}` : `Level ${me.level}`}
            </p>

            {me.next_threshold != null ? (
              <>
                <div
                  style={{
                    height: 6,
                    borderRadius: 9999,
                    background: "var(--border)",
                    overflow: "hidden",
                    marginTop: 12,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 9999,
                      background: "var(--brand-blue)",
                      width: `${Math.min(100, Math.round((me.monthly / me.next_threshold) * 100))}%`,
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--muted)",
                    margin: "6px 0 0",
                  }}
                >
                  {lang === "uk"
                    ? `${me.next_threshold - me.monthly} ₴/міс до рівня ${me.next_level}`
                    : `${me.next_threshold - me.monthly} ₴/mo to Level ${me.next_level}`}
                </p>
              </>
            ) : (
              <p
                style={{
                  fontSize: 12,
                  color: "var(--muted)",
                  margin: "12px 0 0",
                }}
              >
                {lang === "uk" ? "Максимальний рівень" : "Max level"}
              </p>
            )}
          </div>
        )}

        {/* 3. Monobase CTA */}
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => {
              hapticTap();
              openLink(SUBSCRIBE_URL);
            }}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              height: 52,
              borderRadius: "var(--r-pill)",
              background: "var(--brand-blue)",
              color: "#fff",
              border: "none",
              fontFamily: '"e-Ukraine", system-ui, sans-serif',
              fontSize: 16,
              fontWeight: 500,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {t("subscribe_monthly")}
          </button>
          <p
            style={{
              fontSize: 12,
              color: "var(--muted)",
              textAlign: "center",
              margin: "6px 0 0",
            }}
          >
            {t("cancel_anytime")}
          </p>
        </div>

        {/* 4. Your rewards */}
        {me && (
          <>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--tg-hint)",
                margin: "24px 0 12px",
              }}
            >
              {t("your_rewards")}
            </p>
            {REWARDS.filter((r) => r.minLevel <= me.level).map((r) => (
              <button
                key={r.id}
                onClick={
                  r.url
                    ? () => {
                        hapticTap();
                        openLink(r.url!);
                      }
                    : undefined
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "13px 16px",
                  background: "var(--tint)",
                  borderRadius: 12,
                  border: "none",
                  marginBottom: 8,
                  cursor: r.url ? "pointer" : "default",
                  WebkitTapHighlightColor: "transparent",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 15, color: "var(--ink)" }}>
                  {pickLoc(r.name, lang)}
                </span>
                {r.url ? (
                  <span style={{ color: "var(--brand-blue)", fontSize: 16 }}>
                    ↗
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    {t("coming_soon")}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => {
                hapticTap();
                navigate("/levels");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "13px 16px",
                background: "var(--tint)",
                borderRadius: 12,
                border: "none",
                marginBottom: 8,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                textAlign: "left",
              }}
            >
              <span style={{ fontSize: 15, color: "var(--ink)" }}>
                {t("levels_and_merch")}
              </span>
              <span style={{ color: "var(--brand-blue)" }}>→</span>
            </button>
          </>
        )}

        {/* 5. Active fundraisers (de-emphasized) */}
        {fundraisings.length > 0 && (
          <>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--tg-hint)",
                margin: "24px 0 12px",
              }}
            >
              {t("active_fundraisers")}
            </p>
            <div style={{ marginLeft: -16, marginRight: -16 }}>
              <FundraiserCarousel items={fundraisings} />
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
